import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../hooks/useTheme'
import { useLang } from '../context/LangContext'
import { authedFetch } from '../utils/shopifyFetch'

function timeAgo(iso) {
  if (!iso) return 'Never'
  const s = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (s < 60)    return `${s}s ago`
  if (s < 3600)  return `${Math.floor(s/60)}m ago`
  if (s < 86400) return `${Math.floor(s/3600)}h ago`
  return `${Math.floor(s/86400)}d ago`
}

export default function Topbar({ addToast, shop, showNavToggle = false, navOpen = false, onToggleNav }) {
  const location                   = useLocation()
  const navigate                   = useNavigate()
  const [syncing, setSyncing]      = useState(false)
  const [lastSync, setLastSync]    = useState(null)
  const [langOpen, setLangOpen]    = useState(false)
  const { theme, toggle }          = useTheme()
  const { t, lang, setLanguage, LANGUAGES } = useLang()
  const langRef                    = useRef(null)

  const TITLES = {
    '/dashboard': t('nav_dashboard'),
    '/products':  t('nav_products'),
    '/orders':    t('nav_orders'),
    '/profiling': t('nav_profiling'),
    '/activity':  t('nav_activity'),
    '/pricing':   t('nav_pricing'),
    '/settings':  t('nav_settings'),
    '/templates': t('nav_templates'),
    '/help':      t('nav_help'),
  }
  const title = TITLES[location.pathname] || 'SyncE Bridge'

  function loadLastSync() {
    if (!shop) return
    authedFetch(`/api/sync/last?shop=${shop}`)
      .then(r => r.json())
      .then(d => {
        const last = d.runs?.find(r => r.status === 'completed')
        if (last) setLastSync(last.completed_at)
      })
      .catch(() => {})
  }

  useEffect(() => { loadLastSync() }, [shop]) // eslint-disable-line

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  async function handleSync() {
    setSyncing(true)
    try {
      const resp = await authedFetch(`/api/sync/full?shop=${shop}`, { method: 'POST' })
      const d = await resp.json().catch(() => ({}))
      if (!resp.ok) { addToast('error', d.error || 'Sync failed'); return }
      addToast('success', 'Sync started — this runs in the background and may take a minute')
      // Poll for completion so the "last sync" timestamp updates
      if (d.runId) {
        const poll = setInterval(async () => {
          try {
            const r = await authedFetch(`/api/sync/last?shop=${shop}`)
            const data = await r.json()
            const run = (data.runs || []).find(r => r.id === d.runId)
            if (run && run.status !== 'running') {
              clearInterval(poll)
              loadLastSync()
              setSyncing(false)
            }
          } catch { clearInterval(poll); setSyncing(false) }
        }, 3000)
        // Safety: stop polling after 3 minutes regardless
        setTimeout(() => { clearInterval(poll); setSyncing(false) }, 180000)
        return // keep syncing=true until poll resolves
      }
    } catch {
      addToast('warning', 'Sync triggered — check Activity Log for results')
    } finally {
      setSyncing(false)
    }
  }

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]

  return (
    <header className="topbar" style={{ height:'var(--header-h)', background:'var(--bg2)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, zIndex:90 }}>
      {showNavToggle && (
        <button
          type="button"
          className="nav-toggle"
          onClick={onToggleNav}
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          aria-controls="main-nav"
        >
          <span aria-hidden="true">{navOpen ? '✕' : '☰'}</span>
        </button>
      )}
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:16, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</div>
        <div className="topbar-sub" style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--mono)' }}>SyncE Bridge / {title}</div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
        {/* Last sync time */}
        {lastSync && (
          <div className="topbar-lastsync" style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--mono)' }}>
            {t('last_sync')}: {timeAgo(lastSync)}
          </div>
        )}
        {/* Help button — hidden for now */}
        <button
          className="btn btn-sm"
          onClick={handleSync}
          disabled={syncing || !shop}
          aria-label={syncing ? t('syncing') : t('sync_now')}
        >
          <span style={{ display:'inline-block', ...(syncing?{animation:'spin 1s linear infinite'}:{}) }} aria-hidden="true">↺</span>
          {syncing ? t('syncing') : t('sync_now')}
        </button>

        {/* Language picker */}
        <div ref={langRef} style={{ position:'relative' }}>
          <button
            className="btn btn-sm"
            onClick={() => setLangOpen(o => !o)}
            style={{ fontSize:13, gap:4, minWidth:0, padding:'4px 8px' }}
            aria-label={`Language: ${currentLang.label}`}
            aria-expanded={langOpen}
            aria-haspopup="listbox"
          >
            <span aria-hidden="true">{currentLang.flag}</span>
            <span style={{ fontSize:11, marginLeft:4 }}>{currentLang.code.toUpperCase()}</span>
            <span style={{ fontSize:9, marginLeft:2, opacity:.6 }} aria-hidden="true">▾</span>
          </button>
          {langOpen && (
            <div
              role="listbox"
              aria-label="Select language"
              style={{
                position:'absolute', top:'calc(100% + 6px)', right:0,
                background:'var(--bg2)', border:'1px solid var(--border)',
                borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,.18)',
                zIndex:200, minWidth:160, overflow:'hidden'
              }}
            >
              {LANGUAGES.map(l => (
                <div
                  key={l.code}
                  role="option"
                  aria-selected={l.code === lang}
                  onClick={() => { setLanguage(l.code); setLangOpen(false) }}
                  style={{
                    display:'flex', alignItems:'center', gap:10,
                    padding:'8px 14px', fontSize:13, cursor:'pointer',
                    background: l.code === lang ? 'var(--bg3)' : 'transparent',
                    fontWeight: l.code === lang ? 600 : 400,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--bg3)'}
                  onMouseLeave={e => e.currentTarget.style.background = l.code === lang ? 'var(--bg3)' : 'transparent'}
                >
                  <span style={{ fontSize:18 }} aria-hidden="true">{l.flag}</span>
                  <span>{l.label}</span>
                  {l.code === lang && <span style={{ marginLeft:'auto', color:'var(--green)', fontSize:12 }} aria-hidden="true">✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Theme toggle */}
        <div className="theme-toggle">
          <span className="theme-toggle-icon" aria-hidden="true">☾</span>
          <div
            className="theme-toggle-track"
            onClick={toggle}
            role="switch"
            aria-checked={theme === 'light'}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggle()}
          >
            <div className="theme-toggle-thumb" aria-hidden="true">
              {theme === 'dark' ? '☾' : '☀'}
            </div>
          </div>
          <span className="theme-toggle-icon" aria-hidden="true">☀</span>
        </div>
        {/* Shop avatar */}
        <div
          role="img"
          aria-label={`Store: ${shop || 'SyncE Bridge'}`}
          style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,var(--sh),var(--eb))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff', flexShrink:0 }}
        >
          {shop ? shop[0].toUpperCase() : 'S'}
        </div>
      </div>
    </header>
  )
}
